import React, { useState, useEffect } from 'react';
import { Users, UserPlus, Shield, Trash2, Edit3, Key, X, Check, Eye, EyeOff } from 'lucide-react';

function UserManagement({ token, showToast }) {
  const [users, setUsers] = useState([]);
  const [shops, setShops] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [showPass, setShowPass] = useState(false);
  const [formData, setFormData] = useState({ email: '', password: '', role: 'owner', assignedShops: [] });

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const res = await fetch('/api/admin/users', { headers: { 'Authorization': `Bearer ${token}` } });
      const data = await res.json();
      setUsers(data);

      const shopRes = await fetch('/api/admin/shops', { headers: { 'Authorization': `Bearer ${token}` } });
      const shopData = await shopRes.json();
      setShops(shopData);
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  useEffect(() => { fetchUsers(); }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const url = editingUser ? `/api/admin/users/${editingUser.id}` : '/api/admin/users';
    const method = editingUser ? 'PUT' : 'POST';

    try {
      const res = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify(formData)
      });
      if (res.ok) {
        showToast(editingUser ? "Foydalanuvchi yangilandi" : "Yangi foydalanuvchi qo'shildi");
        setShowModal(false);
        setEditingUser(null);
        setFormData({ email: '', password: '', role: 'owner', assignedShops: [] });
        fetchUsers();
      } else {
        const err = await res.json();
        showToast(err.error || "Xatolik", "error");
      }
    } catch (e) { showToast("Server xatosi", "error"); }
  };

  const deleteUser = async (id) => {
    if (!window.confirm("Haqiqatdan ham o'chirmoqchimisiz?")) return;
    try {
      const res = await fetch(`/api/admin/users/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        showToast("O'chirildi");
        fetchUsers();
      }
    } catch (e) { showToast("Xatolik", "error"); }
  };

  return (
    <div className="space-y-8 animate-in fade-in duration-500">
      <div className="flex justify-between items-center bg-slate-900 p-8 rounded-[40px] border border-blue-500/10 shadow-xl">
        <div>
          <h3 className="text-2xl font-black text-white flex items-center gap-3"><Users className="text-blue-400" /> Foydalanuvchilar Boshqaruvi</h3>
          <p className="text-slate-500 font-bold text-sm mt-1">Tizimdagi barcha accountlarni nazorat qilish va yangi do'konlar ochish</p>
        </div>
        <button
          onClick={() => { setEditingUser(null); setFormData({ email: '', password: '', role: 'owner', assignedShops: [] }); setShowModal(true); }}
          className="bg-blue-600 text-white px-8 py-4 rounded-[25px] font-black flex items-center gap-2 hover:bg-blue-700 transition shadow-lg shadow-blue-600/20 active:scale-95"
        >
          <UserPlus size={20} /> FOYDALANUVCHI QO'SHISH
        </button>
      </div>

      <div className="bg-slate-900 rounded-[45px] shadow-2xl border border-blue-500/10 overflow-hidden">
        <table className="w-full text-left">
          <thead>
            <tr className="text-[11px] font-black uppercase text-slate-500 bg-black/20">
              <th className="px-10 py-6 tracking-widest">ID / Email</th>
              <th className="px-10 py-6 text-center tracking-widest">Rol</th>
              <th className="px-10 py-6 text-center tracking-widest">STIR</th>
              <th className="px-10 py-6 text-right tracking-widest">Amallar</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-white/5">
            {users.map((u, idx) => (
              <tr key={idx} className="hover:bg-white/5 transition group">
                <td className="px-10 py-6">
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 rounded-xl bg-slate-800 flex items-center justify-center text-slate-500 border border-white/5 font-black">
                       {u.id}
                    </div>
                    <div className="font-black text-slate-200">{u.email}</div>
                  </div>
                </td>
                <td className="px-10 py-6 text-center">
                  <span className={`px-4 py-1.5 rounded-full text-[10px] font-black uppercase tracking-tighter ${u.role === 'admin' ? 'bg-indigo-500/20 text-indigo-400' : u.role === 'accountant' ? 'bg-amber-500/20 text-amber-400' : 'bg-emerald-500/20 text-emerald-400'}`}>
                    {u.role === 'owner' ? '🛒 Shop Owner' : u.role}
                  </span>
                </td>
                <td className="px-10 py-6 text-center font-bold text-slate-400 font-mono">{u.tin || '—'}</td>
                <td className="px-10 py-6 text-right">
                  <div className="flex justify-end gap-2">
                    <button
                      onClick={() => {
                        setEditingUser(u);
                        setFormData({ email: u.email, role: u.role, password: '', assignedShops: [] });
                        setShowModal(true);
                      }}
                      className="p-3 bg-white/5 text-slate-400 rounded-xl hover:bg-blue-600 hover:text-white transition"
                    >
                      <Edit3 size={18} />
                    </button>
                    <button
                      onClick={() => deleteUser(u.id)}
                      className="p-3 bg-white/5 text-slate-400 rounded-xl hover:bg-rose-600 hover:text-white transition"
                    >
                      <Trash2 size={18} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showModal && (
        <div className="fixed inset-0 bg-slate-950/80 backdrop-blur-md z-[100] flex items-center justify-center p-6">
          <div className="bg-slate-900 rounded-[50px] w-full max-w-lg overflow-hidden shadow-2xl border border-white/5 scale-in">
            <div className="p-8 border-b border-white/5 flex justify-between items-center bg-white/5">
              <h3 className="text-xl font-black text-white">{editingUser ? "Tahrirlash" : "Yangi Foydalanuvchi"}</h3>
              <button onClick={() => setShowModal(false)} className="p-2 text-slate-500 hover:text-white transition"><X /></button>
            </div>
            <form onSubmit={handleSubmit} className="p-8 space-y-5">
               <div className="space-y-1">
                 <p className="text-[10px] font-black text-slate-500 uppercase tracking-widest ml-1">Email Manzili</p>
                 <input
                  type="email"
                  className="w-full p-4 bg-slate-950 border border-white/5 rounded-2xl outline-none focus:border-blue-500/20 font-bold text-white"
                  value={formData.email}
                  onChange={e => setFormData({...formData, email: e.target.value})}
                  required
                 />
               </div>
               <div className="space-y-1">
                 <p className="text-[10px] font-black text-slate-500 uppercase tracking-widest ml-1">{editingUser ? "Yangi Parol (ixtiyoriy)" : "Parol"}</p>
                 <div className="relative">
                   <input
                    type={showPass ? "text" : "password"}
                    className="w-full p-4 bg-slate-950 border border-white/5 rounded-2xl outline-none focus:border-blue-500/20 font-bold text-white"
                    value={formData.password}
                    onChange={e => setFormData({...formData, password: e.target.value})}
                    required={!editingUser}
                   />
                   <button type="button" onClick={() => setShowPass(!showPass)} className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-600">
                      {showPass ? <EyeOff size={18}/> : <Eye size={18}/>}
                   </button>
                 </div>
               </div>
               <div className="space-y-1">
                 <p className="text-[10px] font-black text-slate-500 uppercase tracking-widest ml-1">Tizimdagi Roli</p>
                 <select
                  className="w-full p-4 bg-slate-950 border border-white/5 rounded-2xl outline-none focus:border-blue-500/20 font-bold text-white appearance-none"
                  value={formData.role}
                  onChange={e => setFormData({...formData, role: e.target.value})}
                 >
                   <option value="owner">Do'kon Egasi (Owner)</option>
                   <option value="accountant">Buxgalter (Accountant)</option>
                   <option value="admin">Administrator</option>
                 </select>
               </div>

               {formData.role === 'accountant' && (
                 <div className="space-y-1">
                   <p className="text-[10px] font-black text-slate-500 uppercase tracking-widest ml-1">Biriktirilgan Do'konlar</p>
                   <div className="grid grid-cols-1 gap-2 max-h-40 overflow-y-auto p-2 bg-black/20 rounded-2xl border border-white/5">
                      {shops.map(s => (
                        <label key={s.id} className="flex items-center gap-3 p-4 bg-white/5 rounded-xl cursor-pointer hover:bg-white/10 transition border border-white/5">
                           <input
                            type="checkbox"
                            className="w-4 h-4 rounded border-white/10"
                            checked={formData.assignedShops.includes(s.id)}
                            onChange={e => {
                              const newShops = e.target.checked
                                ? [...formData.assignedShops, s.id]
                                : formData.assignedShops.filter(id => id !== s.id);
                              setFormData({...formData, assignedShops: newShops});
                            }}
                           />
                           <span className="text-[11px] font-black text-slate-300 uppercase truncate">{s.email}</span>
                        </label>
                      ))}
                   </div>
                 </div>
               )}

               <button type="submit" className="w-full bg-blue-600 text-white py-5 rounded-2xl font-black hover:bg-blue-700 transition shadow-xl mt-4 uppercase tracking-widest shadow-blue-600/10">
                 {editingUser ? "O'ZGARISHLARNI SAQLASH" : "FOYDALANUVCHINI YARATISH"}
               </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default UserManagement;
